import {
    DocumentSignature,
    DocumentSaveSignatureRequest,
    FetchState,
    SigningDocument,
    SigningDocumentState,
    SignedDocumentKey,
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: SigningDocumentState = {
    documentFetchState: FetchState.Uninitialized,
    allDocumentsFetchState: FetchState.Uninitialized,
    allDocumentsAdminFetchState: FetchState.Uninitialized,
    documents: [],
    documentsAdmin: [],
    document: null,
    signatureFetchState: FetchState.Uninitialized,
    signature: null,
    signedDocumentKey: null,
    signedDocumentKeyFetchState: FetchState.Uninitialized
};

export const useSigningDocumentStore = defineStore("signingDocument", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        isDocumentLoaded: state => state.documentFetchState === FetchState.Loaded,
        allDocumentsLoaded: state => state.allDocumentsFetchState === FetchState.Loaded,
        allDocumentsLoadedAdmin: state => state.allDocumentsAdminFetchState === FetchState.Loaded,
        currentDocument: state => state.document,
        isSigned: state => state.signatureFetchState === FetchState.Loaded && state.signature !== null,
        signedKey: state => {
            if (state.signedDocumentKey) {
                return state.signedDocumentKey.documentKey;
            }

            return null;
        },
        isSignedKeyLoaded: state => state.signedDocumentKeyFetchState === FetchState.Loaded
    },
    actions: {
        setDocumentFetchState (fetchState: FetchState) {
            this.documentFetchState = fetchState;
        },
        setSignatureFetchState (fetchState: FetchState) {
            this.signatureFetchState = fetchState;
        },
        setAllDocumentsFetchState (fetchState: FetchState) {
            this.allDocumentsFetchState = fetchState;
        },
        setAllDocumentsAdminFetchState (fetchState: FetchState) {
            this.allDocumentsAdminFetchState = fetchState;
        },
        setRecords (documents: SigningDocument[]) {
            this.documents = [ ...documents ];
        },
        setRecordsAdmin (documentsAdmin: SigningDocument[]) {
            this.documentsAdmin = [ ...documentsAdmin ];
        },
        setCurrentDocument (document: SigningDocument) {
            this.document = document;
        },
        setSignature (signature: DocumentSignature) {
            this.signature = signature;
        },
        setSignedKey (signedDocumentKey: SignedDocumentKey) {
            this.signedDocumentKey = signedDocumentKey;
        },
        setSignedKeyFetchState (fetchState: FetchState) {
            this.signedDocumentKeyFetchState = fetchState;
        },
        deleteSignature (key: string): Promise<void> {
            return new Promise<void>(resolve => UserService.deleteSignature(key)
                .then(_ => {
                    this.setSignature(null);
                    resolve();
                })
                .catch(e => storeHelper.handleError({ error: e, rethrow: true }))
            );
        },
        get ({ documentKey, version }) {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getSigningDocument(documentKey, version).then(resp => {
                this.setCurrentDocument(resp.data);
                this.setDocumentFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getAsAdmin ({ documentKey, version }) {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getSigningDocumentAdmin(documentKey, version).then(resp => {
                this.setCurrentDocument(resp.data);
                this.setDocumentFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getById ({ documentId, version }) {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getSigningDocumentById(documentId, version).then(resp => {
                this.setCurrentDocument(resp.data);
                this.setDocumentFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getByIdAdmin ({ documentId, version }) {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getSigningDocumentByIdAdmin(documentId, version).then(resp => {
                this.setCurrentDocument(resp.data);
                this.setDocumentFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getDocumentLatestVersion (documentKey) {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getDocumentLatestVersion(documentKey)
                .then(r => this.setCurrentDocument(r.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }))
                .finally(() => this.setDocumentFetchState(FetchState.Loaded));
        },
        getDocumentLatestVersionAdmin (documentKey) {
            this.setDocumentFetchState(FetchState.Loading);
            return UserService.getDocumentLatestVersionAdmin(documentKey)
                .then(r => this.setCurrentDocument(r.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }))
                .finally(() => this.setDocumentFetchState(FetchState.Loaded));
        },
        getSignature (documentKey) {
            this.setSignatureFetchState(FetchState.Loading);
            return UserService.getSignature(documentKey).then(resp => {
                this.setSignature(resp.data);
                this.setSignatureFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getSignedDocumentKey () {
            this.setSignedKeyFetchState(FetchState.Loading);
            return UserService.getUserSignedDocumentKey().then(resp => {
                this.setSignedKey(resp.data);
                this.setSignedKeyFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        saveSignature (request: DocumentSaveSignatureRequest): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                return UserService.saveSignature(request).then(resp => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess(`Document has been signed.`);
                    resolve();
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            });
        },
        save (document: SigningDocument): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                if (this.documentFetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setDocumentFetchState(FetchState.Loading);
                    const notifierStore = useNotifierStore();
                    return UserService.saveSigningDocument(document).then(resp => {
                        this.setDocumentFetchState(FetchState.Loaded);
                        notifierStore.setSuccess(`Document has been saved.`);
                        resolve();
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
        loadAll () {
            return new Promise<void>((resolve, reject) => {
                if (this.allDocumentsFetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setAllDocumentsFetchState(FetchState.Loading);
                    return UserService.getSigningDocuments().then(resp => {
                        this.setRecords(resp.data);
                        this.setAllDocumentsFetchState(FetchState.Loaded);
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
        loadAllAdmin () {
            return new Promise<void>((resolve, reject) => {
                if (this.allDocumentsAdminFetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setAllDocumentsAdminFetchState(FetchState.Loading);
                    return UserService.getSigningDocuments().then(resp => {
                        this.setRecordsAdmin(resp.data);
                        this.setAllDocumentsAdminFetchState(FetchState.Loaded);
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        }
    }
});